import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryClient } from '@/lib/queryClient';
import type { Plan } from '@/types';
import type { TierPrices } from '@/lib/pricing';

export interface PlanWithId extends Plan {
  dbId: string;
  goalKey: string;
  pricePerMealCents: number;
  monthlyRatio: number;
  tierPrices: TierPrices | null;
}

const PLANS_QUERY_KEY = ['plans'];

async function fetchPlans(): Promise<PlanWithId[]> {
  const { data: rows, error } = await supabase
    .from('plans')
    .select('id, slug, goal_key, name, description, calorie_range, meals_per_day_min, meals_per_day_max, price_cents, monthly_price_cents, monthly_savings_cents, price_per_meal_cents, image_url, features, is_popular, tier_prices')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;

  return (rows ?? []).map(row => ({
    dbId:              row.id,
    goalKey:           row.goal_key ?? '',
    id:                row.slug ?? row.id,
    name:              row.name,
    description:       row.description ?? '',
    calorieRange:      row.calorie_range ?? '',
    mealsPerDay:       [row.meals_per_day_min ?? 2, row.meals_per_day_max ?? 3],
    weeklyPrice:       (row.price_cents ?? 0) / 100,
    monthlyPrice:      (row.monthly_price_cents ?? 0) / 100,
    monthlySavings:    (row.monthly_savings_cents ?? 0) / 100,
    pricePerMealCents: row.price_per_meal_cents ?? 0,
    monthlyRatio:      (row.price_cents ?? 0) > 0
      ? (row.monthly_price_cents ?? 0) / (row.price_cents ?? 1)
      : 4,
    tierPrices:        (row.tier_prices as TierPrices | null) ?? null,
    image:             row.image_url ?? '',
    features:          row.features ?? [],
    popular:           row.is_popular ?? false,
  }));
}

export function prefetchPlans() {
  queryClient.prefetchQuery({ queryKey: PLANS_QUERY_KEY, queryFn: fetchPlans });
}

export function usePlans() {
  const { data, isLoading, error } = useQuery({
    queryKey: PLANS_QUERY_KEY,
    queryFn: fetchPlans,
  });

  if (error) console.error('[usePlans]', error);

  return { data: data ?? [], isLoading };
}
